import React, { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AdyenAccount } from '../services/api';
import { CaseSteps, type CaseStep, type CaseStepState } from './ObjectionCase';
import {
  ADYEN_KYC_BADGE,
  capabilityIsValid,
  deriveAdyenKyc,
  isMockAdyenOnboardingUrl
} from '../utils/adyenKyc';

// Maps the Adyen kycStatus enum to i18n keys resolved with t() at the render site.
const KYC_STATUS_LABEL_KEYS: Record<string, string> = {
  passed: 'kycStatus.statusPassed',
  in_review: 'kycStatus.statusInReview',
  pending: 'kycStatus.statusPending',
  failed: 'kycStatus.statusFailed'
};

export interface KycStatusMessage {
  type: 'success' | 'error';
  text: string;
}

interface KycStatusPanelProps {
  account: AdyenAccount | null;
  loading: boolean;
  /** Warnings and onboarding actions are unavailable in mock runtime. Status stays read-only. */
  showOnboardingPrompts?: boolean;
  message?: KycStatusMessage | null;
  /** Read-only "Uppdatera status" (sync from Adyen, changes nothing on the company). */
  showRefreshButton?: boolean;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  /** "Starta kundkännedom" — omitted in the Admin view (staff must not trigger it). */
  showStartButton?: boolean;
  onStart?: () => void;
  isStarting?: boolean;
  /** Link out to the Adyen dashboard. */
  showOpenAdyen?: boolean;
  /** Hide technical identifiers and capabilities behind a disclosure. */
  technicalDetailsCollapsible?: boolean;
  /**
   * The Hantverkare's own view (ClickUp 869fncme5): status, steps and what is
   * paused in plain words, with the figures under the technical details.
   * Admin leaves it off and sees the panel as before.
   */
  plainLanguage?: boolean;
}

const PLAIN_STATUS_TONE: Record<string, string> = {
  pending: 'is-waiting',
  in_review: 'is-attest',
  passed: 'is-approved',
  failed: 'is-disputed'
};

/** Where the company stands, in the plain view's four steps. */
const plainStep = (account: AdyenAccount, payoutReady: boolean) => {
  if (payoutReady) return 4;
  if (account.kycStatus === 'passed') return account.transferInstrumentId ? 3 : 2;
  return account.kycStatus === 'pending' ? 0 : 1;
};

/**
 * Presentational KYC (Kundkännedom) panel shared between the company's own
 * settings page and the Admin company overview. The Admin view renders it
 * read-only (no "Starta kundkännedom").
 */
export function KycStatusPanel({
  account,
  loading,
  showOnboardingPrompts = true,
  message,
  showRefreshButton = true,
  onRefresh,
  isRefreshing = false,
  showStartButton = false,
  onStart,
  isStarting = false,
  showOpenAdyen = true,
  technicalDetailsCollapsible = false,
  plainLanguage = false
}: KycStatusPanelProps) {
  const { t } = useTranslation();
  const [technicalDetailsExpanded, setTechnicalDetailsExpanded] = useState(false);
  const technicalDetailsId = useId();
  const { requiredCapabilities, validCapabilities, hasLocalMockIds, payoutReady } =
    deriveAdyenKyc(account);
  const canOpenAdyen =
    showOnboardingPrompts && showOpenAdyen && account?.onboardingUrl && !isMockAdyenOnboardingUrl(account.onboardingUrl);

  const figures = account && (
    <>
      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <div className="border rounded p-3 h-100">
            <small className="text-muted">{t('kycStatus.kycTitle')}</small>
            <div className="mt-2">
              <span className={`badge ${ADYEN_KYC_BADGE[account.kycStatus] || 'bg-secondary'}`}>
                {KYC_STATUS_LABEL_KEYS[account.kycStatus]
                  ? t(KYC_STATUS_LABEL_KEYS[account.kycStatus])
                  : account.kycStatus}
              </span>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="border rounded p-3 h-100">
            <small className="text-muted">Capabilities</small>
            <div className="fw-bold mt-1">{validCapabilities.length}/{requiredCapabilities.length}</div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="border rounded p-3 h-100">
            <small className="text-muted">{t('kycStatus.balanceLabel')}</small>
            <div className="fw-bold mt-1">{new Intl.NumberFormat('sv-SE').format(account.balanceSek || 0)} kr</div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="border rounded p-3 h-100">
            <small className="text-muted">{t('kycStatus.syncedLabel')}</small>
            <div className="fw-semibold mt-1">{account.lastSyncedAt ? new Date(account.lastSyncedAt).toLocaleString('sv-SE') : '—'}</div>
          </div>
        </div>
      </div>
    </>
  );

  const technicalDetails = account && (
    <div className="row g-3">
      <div className="col-lg-6">
        <h6 className="fw-bold mb-3">{t('kycStatus.adyenIdsTitle')}</h6>
        <dl className="row small mb-0">
          <dt className="col-sm-5 text-muted">Legal Entity</dt>
          <dd className="col-sm-7 font-monospace text-break">{account.adyenLegalEntityId || '—'}</dd>
          <dt className="col-sm-5 text-muted">Account Holder</dt>
          <dd className="col-sm-7 font-monospace text-break">{account.adyenAccountHolderId}</dd>
          <dt className="col-sm-5 text-muted">Balance Account</dt>
          <dd className="col-sm-7 font-monospace text-break">{account.adyenBalanceAccountId}</dd>
          <dt className="col-sm-5 text-muted">{t('kycStatus.technicalReference')}</dt>
          <dd className="col-sm-7 font-monospace text-break">{account.balanceAccountReference || '—'}</dd>
          <dt className="col-sm-5 text-muted">Transfer Instrument</dt>
          <dd className="col-sm-7 font-monospace text-break">{account.transferInstrumentId || '—'}</dd>
        </dl>
      </div>
      <div className="col-lg-6">
        <h6 className="fw-bold mb-3">{t('kycStatus.requiredCapabilitiesTitle', 'Obligatoriska capabilities')}</h6>
        <div className="d-flex flex-column gap-2">
          {requiredCapabilities.map((capability) => {
            const valid = capabilityIsValid(account.capabilities?.[capability]);
            return (
              <div key={capability} className="d-flex align-items-center justify-content-between gap-2 border rounded px-3 py-2">
                <span className="font-monospace small text-break">{capability}</span>
                <span className={`badge flex-shrink-0 ${valid ? 'bg-success' : 'bg-warning text-dark'}`}>{valid ? 'valid' : 'pending'}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  return (
    <div className="card-body">
      <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-4">
        <div>
          <h6 className="fw-bold mb-1">{t('kycStatus.kycTitle')}</h6>
          <div className="text-muted small">{t('kycStatus.kycSubtitle')}</div>
        </div>
        <div className="d-flex flex-wrap gap-2">
          {canOpenAdyen && (
            <a className="btn btn-outline-primary btn-sm" href={account!.onboardingUrl!} target="_blank" rel="noopener noreferrer">
              <i className="feather-external-link me-2"></i>{t('kycStatus.openAdyen')}
            </a>
          )}
          {showRefreshButton && (
            <button className="btn btn-outline-secondary btn-sm" onClick={onRefresh} disabled={isRefreshing || loading}>
              {isRefreshing ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="feather-refresh-cw me-2"></i>}
              {t('kycStatus.refreshStatus')}
            </button>
          )}
          {showOnboardingPrompts && showStartButton && (
            <button className="btn btn-primary btn-sm" onClick={onStart} disabled={isStarting || loading}>
              {isStarting ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="feather-play me-2"></i>}
              {t('kycStatus.startKyc')}
            </button>
          )}
        </div>
      </div>

      {message && (
        <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} py-2 small`}>
          {message.text}
        </div>
      )}

      {loading ? (
        <div className="text-center py-4">
          <div className="spinner-border spinner-border-sm text-primary" />
        </div>
      ) : account ? (
        <>
          {plainLanguage && (() => {
            const current = plainStep(account, payoutReady);
            const failedStep: CaseStepState | undefined = account.kycStatus === 'failed' ? 'warning' : undefined;
            const plainSteps: CaseStep[] = (['submitted', 'reviewing', 'bankAccount', 'ready'] as const).map((key, index) => ({
              label: t(`kycStatus.plain.steps.${key}`),
              ...(index === 1 && failedStep ? { state: failedStep } : {})
            }));
            const status = KYC_STATUS_LABEL_KEYS[account.kycStatus] ? account.kycStatus : 'pending';
            return (
              <div className="d-flex flex-column gap-3 mb-4" data-testid="kyc-plain-status">
                <span className={`objection-status-pill ${PLAIN_STATUS_TONE[status]} align-self-start`}>
                  {t(KYC_STATUS_LABEL_KEYS[status])}
                </span>
                <div>
                  <h3 className="objection-section-title fs-5 mb-2">
                    {payoutReady ? t('kycStatus.plain.readyTitle') : t(`kycStatus.plain.${status}.title`)}
                  </h3>
                  <p className="mb-0">
                    {payoutReady ? t('kycStatus.plain.readyText') : t(`kycStatus.plain.${status}.text`)}
                  </p>
                </div>
                <CaseSteps steps={plainSteps} currentStep={current} />
                {!payoutReady && showOnboardingPrompts && (
                  <div className="objection-money" data-testid="kyc-plain-paused">
                    <i className="feather-lock" aria-hidden="true"></i>
                    <p className="mb-0">
                      <span className="fw-semibold">{t('kycStatus.plain.lockTitle')}</span> {t('kycStatus.plain.lockText')}
                    </p>
                  </div>
                )}
              </div>
            );
          })()}

          {!plainLanguage && (payoutReady || showOnboardingPrompts) && (
            <div className={`alert ${payoutReady ? 'alert-success' : 'alert-warning'} d-flex align-items-center gap-2`}>
              <i className={`feather-${payoutReady ? 'check-circle' : 'lock'}`}></i>
              <div>
                <div className="fw-semibold">{payoutReady ? t('kycStatus.payoutAllowed', 'Utbetalning tillåten') : t('kycStatus.payoutBlocked')}</div>
                <div className="small">
                  {payoutReady
                    ? t('kycStatus.payoutReadyBody')
                    : hasLocalMockIds
                      ? t('kycStatus.payoutMockBody')
                      : t('kycStatus.payoutBlockedBody')}
                </div>
              </div>
            </div>
          )}

          {!plainLanguage && figures}

          {technicalDetailsCollapsible ? (
            <div className="border rounded overflow-hidden">
              <button
                type="button"
                className="btn btn-light w-100 text-start rounded-0 p-3 d-flex align-items-center justify-content-between gap-3"
                aria-expanded={technicalDetailsExpanded}
                aria-controls={technicalDetailsId}
                onClick={() => setTechnicalDetailsExpanded((expanded) => !expanded)}
              >
                <span>
                  <span className="d-block fw-bold">
                    {plainLanguage ? t('kycStatus.plain.technicalTitle') : t('kycStatus.adyenKycDetailsTitle', 'Adyen KYC-detaljer')}
                  </span>
                  <span className="d-block text-muted small fw-normal mt-1">
                    {plainLanguage ? t('kycStatus.plain.technicalHelp') : t('kycStatus.adyenKycDetailsHelp')}
                  </span>
                </span>
                <i
                  className={`feather-chevron-${technicalDetailsExpanded ? 'up' : 'down'} flex-shrink-0`}
                  aria-hidden="true"
                ></i>
              </button>
              {technicalDetailsExpanded && (
                <div id={technicalDetailsId} className="border-top p-3">
                  {plainLanguage && figures}
                  {technicalDetails}
                </div>
              )}
            </div>
          ) : technicalDetails}
        </>
      ) : (
        showOnboardingPrompts && <div className="alert alert-warning mb-0">{t('kycStatus.noStatus')}</div>
      )}
    </div>
  );
}
